import BaseInteriorScene from "../BaseInteriorScene";
import rawQuestSource from "./voidQuest-source.json";
import questHallBg from "../GameMap/InfiNFT Maps/voidQuest.png";
import questMasterNpcPng from "../../../@shared/assets/GameMaps/tilesets/interior/questMaster.png";
import { getVoidCityNpcDialogue } from "../../../@shared/utils/npcDialogs";
import { useStoryStore } from "../../../@shared/store/useStoryStore";
import { createWalkAnimations } from "../../../@shared/utils/SceneHelpers";
import {
  SPRITES_MAP,
  getAvatarFrameForDirection,
  getAvatarSpriteConfig,
} from "../../../@shared/utils/SpritesMap";
import {
} from "../../../@shared/utils/storySideQuests";

const QUESTMASTER_NPC_KEY = "questMasterNpcPortrait";
const STORY_GLYDE_KEY = "questHallStoryGlyde";
const QUESTMASTER_NPC_SCALE = 0.05;
const QUESTMASTER_FEET_OFFSET_Y = 34;
const QUESTMASTER_FOREGROUND_DEPTH = 80;
const QUESTMASTER_FOREGROUND_HEIGHT = 40;
const STORY_GLYDE_SCALE = getAvatarSpriteConfig("glyde").worldScale || 1;
const STORY_GLYDE_STAGE_POSITION = Object.freeze({ x: 170, y: 336 });
const STORY_GLYDE_INTERACTION_SIZE = Object.freeze({ width: 64, height: 56 });
const STORY_GLYDE_INTERACTION_OFFSET_Y = 28;
const STORY_GLYDE_RETURN_MISSION_ID = "act-1-mission-15";
const STORY_GLYDE_ACT_TWO_MISSION_ID = "act-2-mission-1";
const STORY_QUESTMASTER_ACT_TWO_MISSION_ID = "act-2-mission-2";
const STORY_VOID_CAVE_APPROVAL_MISSION_ID = "act-2-mission-3";
const LEGACY_GLYDE_WAIT_MISSION_ID = "act-1-mission-11";
const STORY_GLYDE_IDLE_DIRECTION = 0;
const STORY_GLYDE_AUTO_APPROACH_CUE_ID = "glyde-approach";
const STORY_GLYDE_SIDEQUEST_APPROVAL_CUE_ID = "glyde-enter";
const STORY_GLYDE_ANIMATION_PREFIX = "questhall-story-glyde";
const STORY_GLYDE_PLAYER_SIDE_OFFSET = Object.freeze({ x: -54, y: 18 });
const STORY_GLYDE_DEFAULT_SCENE_STATE = Object.freeze({
  x: STORY_GLYDE_STAGE_POSITION.x,
  y: STORY_GLYDE_STAGE_POSITION.y,
  facingDirection: STORY_GLYDE_IDLE_DIRECTION,
});

const questSource = {
  ...rawQuestSource,
  layers: rawQuestSource.layers.map((layer) => {
    if (layer.type !== "objectgroup" || layer.name !== "TestCollisions") {
      return layer;
    }

    return {
      ...layer,
      objects: layer.objects.map((object) => {
        if (object.name === "merchant") {
          return { ...object, name: "questmaster" };
        }
        if (object.name === "merchant-interaction") {
          return { ...object, name: "questmaster-interaction" };
        }
        return object;
      }),
    };
  }),
};

export default class QuestHallScene extends BaseInteriorScene {
  constructor() {
    super(
      "QuestHallScene",
      BaseInteriorScene.extractCollisionDataFromMapSource(
        questSource,
        "TestCollisions",
      ),
      questHallBg,
      {
        exit: { to: "VoidCity2Scene", spawnAt: { x: 698, y: 791 } },
      },
      getVoidCityNpcDialogue,
    );
  }

  preload() {
    super.preload();
    this.load.image(QUESTMASTER_NPC_KEY, questMasterNpcPng);
    this.load.spritesheet(STORY_GLYDE_KEY, SPRITES_MAP.glyde, {
      frameWidth: getAvatarSpriteConfig("glyde").frameWidth,
      frameHeight: getAvatarSpriteConfig("glyde").frameHeight,
    });
  }

  create(data) {
    super.create(data);
    this.storyGlyde = null;
    this.storyGlydeWalking = false;
    this.storyGlydeApproachComplete = false;
    this.storyGlydeCueKey = "";

    this.createAmbientInteriorNpc({
      textureKey: "interiorAmbientNpcE",
      x: 116,
      y: 298,
      facing: "right",
    });
    this.createAmbientInteriorNpc({
      textureKey: "interiorAmbientNpcA",
      x: 588,
      y: 298,
      facing: "left",
    });

    const collisionLayer = questSource.layers.find(
      (layer) => layer.type === "objectgroup" && layer.name === "TestCollisions",
    );
    const questMasterAnchor = collisionLayer?.objects?.find(
      (object) => object.name === "questmaster",
    );
    const deskAnchor = collisionLayer?.objects?.find(
      (object) => object.name === "desk",
    );

    if (!questMasterAnchor) return;

    const anchorX = questMasterAnchor.x + questMasterAnchor.width / 2;
    const npcY =
      questMasterAnchor.y + questMasterAnchor.height + QUESTMASTER_FEET_OFFSET_Y;

    this.add
      .image(anchorX, npcY, QUESTMASTER_NPC_KEY)
      .setOrigin(0.5, 1)
      .setScale(QUESTMASTER_NPC_SCALE)
      .setDepth(70);

    if (deskAnchor) {
      this.add
        .image(0, 0, `${this.scene.key}_bg`)
        .setOrigin(0)
        .setDepth(QUESTMASTER_FOREGROUND_DEPTH)
        .setCrop(
          deskAnchor.x,
          deskAnchor.y,
          deskAnchor.width,
          QUESTMASTER_FOREGROUND_HEIGHT,
        );
    }

    const storyStore = useStoryStore.getState();
    const currentLaunchTarget =
      data?.storyLaunchTarget ||
      (typeof window !== "undefined" ? window.__storyLaunchTarget : null) ||
      storyStore?.currentLaunchTarget ||
      null;
    const currentMissionId =
      currentLaunchTarget?.missionId || storyStore?.currentMissionId || "";
    const storedStoryGlydeState =
      currentLaunchTarget?.world?.storyActors?.glyde || null;
    const activeStoryGlydeState = {
      ...STORY_GLYDE_DEFAULT_SCENE_STATE,
      ...(storedStoryGlydeState || {}),
    };
    const isStoryMode = this.userData?.activeGameMode === "story";
    const shouldStageStoryGlyde =
      isStoryMode &&
      (
        (
          currentMissionId === STORY_GLYDE_RETURN_MISSION_ID &&
          ["npc", "awaitingContent"].includes(currentLaunchTarget?.kind)
        ) ||
        (
          currentMissionId === STORY_GLYDE_ACT_TWO_MISSION_ID &&
          ["npc", "awaitingContent"].includes(currentLaunchTarget?.kind)
        ) ||
        (
          currentMissionId === STORY_QUESTMASTER_ACT_TWO_MISSION_ID &&
          ["npc", "awaitingContent"].includes(currentLaunchTarget?.kind)
        ) ||
        (
          currentMissionId === LEGACY_GLYDE_WAIT_MISSION_ID &&
          currentLaunchTarget?.kind === "awaitingContent"
        )
      );
    const shouldEnableStoryGlydeInteraction =
      isStoryMode &&
      [STORY_GLYDE_RETURN_MISSION_ID, STORY_GLYDE_ACT_TWO_MISSION_ID].includes(
        currentMissionId,
      ) &&
      currentLaunchTarget?.kind === "npc" &&
      currentLaunchTarget?.startMode !== "auto" &&
      String(currentLaunchTarget?.npc || "").trim().toLowerCase() === "glyde";
    const shouldAutoApproachStoryGlyde =
      isStoryMode &&
      currentMissionId === STORY_GLYDE_RETURN_MISSION_ID &&
      currentLaunchTarget?.kind === "npc" &&
      currentLaunchTarget?.startMode === "auto" &&
      String(currentLaunchTarget?.npc || "").trim().toLowerCase() === "glyde";

    const dispatchStoryGlydeWorldState = ({
      glydeState = activeStoryGlydeState,
      playerState = null,
    } = {}) => {
      if (!isStoryMode || !currentLaunchTarget?.world?.mapId) return;

      window.dispatchEvent(
        new CustomEvent("story-world-location", {
          detail: {
            world: {
              ...currentLaunchTarget.world,
              x: Number.isFinite(Number(playerState?.x))
                ? Number(playerState.x)
                : currentLaunchTarget?.world?.x ?? null,
              y: Number.isFinite(Number(playerState?.y))
                ? Number(playerState.y)
                : currentLaunchTarget?.world?.y ?? null,
              facingDirection:
                typeof playerState?.facingDirection === "number"
                  ? playerState.facingDirection
                  : currentLaunchTarget?.world?.facingDirection ?? null,
              storyActors: {
                ...(currentLaunchTarget?.world?.storyActors || {}),
                glyde: {
                  x: Number(glydeState?.x ?? STORY_GLYDE_STAGE_POSITION.x),
                  y: Number(glydeState?.y ?? STORY_GLYDE_STAGE_POSITION.y),
                  facingDirection:
                    typeof glydeState?.facingDirection === "number"
                      ? glydeState.facingDirection
                      : STORY_GLYDE_IDLE_DIRECTION,
                },
              },
            },
          },
        }),
      );
    };

    createWalkAnimations(
      this,
      STORY_GLYDE_KEY,
      "glyde",
      STORY_GLYDE_ANIMATION_PREFIX,
    );

    const buildStoryGlyde = (position = activeStoryGlydeState) => {
      if (this.storyGlyde) return this.storyGlyde;

      this.storyGlyde = this.add
        .sprite(position.x, position.y, STORY_GLYDE_KEY, 0)
        .setOrigin(0.5, 1)
        .setScale(STORY_GLYDE_SCALE)
        .setFrame(
          getAvatarFrameForDirection(
            "glyde",
            typeof position?.facingDirection === "number"
              ? position.facingDirection
              : STORY_GLYDE_IDLE_DIRECTION,
            0,
          ),
        )
        .setDepth(position.y + 24);

      return this.storyGlyde;
    };

    const syncStoryGlydeDepth = () => {
      if (!this.storyGlyde) return;
      this.storyGlyde.setDepth(this.storyGlyde.y + 24);
    };

    const getStoryGlydeTargetPosition = () => ({
      x: (this.player?.x ?? STORY_GLYDE_STAGE_POSITION.x) + STORY_GLYDE_PLAYER_SIDE_OFFSET.x,
      y: (this.player?.y ?? STORY_GLYDE_STAGE_POSITION.y) + STORY_GLYDE_PLAYER_SIDE_OFFSET.y,
    });

    const completeStoryGlydeApproachCue = ({
      missionId = STORY_GLYDE_RETURN_MISSION_ID,
      cueId = STORY_GLYDE_AUTO_APPROACH_CUE_ID,
      lineIndex = 0,
      requestDialogOnComplete = false,
      npc = "glyde",
    } = {}) => {
      window.dispatchEvent(
        new CustomEvent("story-scene-cue-complete", {
          detail: {
            missionId,
            cueId,
            lineIndex,
            sceneKey: this.scene.key,
          },
        }),
      );

      if (requestDialogOnComplete) {
        window.setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent("story-world-dialog-request", {
              detail: {
                missionId,
                sceneKey: this.scene.key,
                npc,
                cueId,
                lineIndex,
              },
            }),
          );
        }, 100);
      }
    };

    const maybeStartStoryGlydeApproach = ({
      missionId = STORY_GLYDE_RETURN_MISSION_ID,
      cueId = STORY_GLYDE_AUTO_APPROACH_CUE_ID,
      lineIndex = 0,
      requestDialogOnComplete = false,
    } = {}) => {
      if (this.storyGlydeWalking) return;
      if (this.storyGlydeApproachComplete) {
        completeStoryGlydeApproachCue({
          missionId,
          cueId,
          lineIndex,
          requestDialogOnComplete,
        });
        return;
      }

      const glyde = buildStoryGlyde(STORY_GLYDE_STAGE_POSITION);
      const targetPosition = getStoryGlydeTargetPosition();
      const horizontalAnimation =
        targetPosition.x >= glyde.x
          ? `${STORY_GLYDE_ANIMATION_PREFIX}-right`
          : `${STORY_GLYDE_ANIMATION_PREFIX}-left`;

      this.storyGlydeWalking = true;
      glyde.play(horizontalAnimation, true);
      this.tweens.killTweensOf(glyde);
      this.tweens.add({
        targets: glyde,
        x: targetPosition.x,
        y: targetPosition.y,
        duration: 1400,
        ease: "Sine.easeInOut",
        onUpdate: () => {
          syncStoryGlydeDepth();
        },
        onComplete: () => {
          this.storyGlydeWalking = false;
          this.storyGlydeApproachComplete = true;
          glyde.anims?.stop?.();
          glyde.setFrame(
            getAvatarFrameForDirection("glyde", 90, 0),
          );
          syncStoryGlydeDepth();
          dispatchStoryGlydeWorldState({
            glydeState: {
              x: glyde.x,
              y: glyde.y,
              facingDirection: 90,
            },
            playerState: {
              x: this.player?.x,
              y: this.player?.y,
              facingDirection:
                typeof this.lastFacingDirection === "number"
                  ? this.lastFacingDirection
                  : 0,
            },
          });
          console.log("[StoryQuestHall] Glyde approach cue complete", {
            missionId,
            cueId,
            lineIndex,
          });
          completeStoryGlydeApproachCue({
            missionId,
            cueId,
            lineIndex,
            requestDialogOnComplete,
          });
        },
      });
    };

    const handleStorySceneCue = (event) => {
      const missionId = String(event?.detail?.missionId || "");
      const cueId = String(event?.detail?.cue?.id || "");
      const lineIndex = Number(event?.detail?.lineIndex ?? 0);
      const isReturnCue =
        missionId === STORY_GLYDE_RETURN_MISSION_ID &&
        cueId === STORY_GLYDE_AUTO_APPROACH_CUE_ID;
      const isSideQuestApprovalCue =
        missionId === STORY_VOID_CAVE_APPROVAL_MISSION_ID &&
        cueId === STORY_GLYDE_SIDEQUEST_APPROVAL_CUE_ID;

      if (!isReturnCue && !isSideQuestApprovalCue) {
        return;
      }

      const cueKey = `${missionId}:${cueId}:${lineIndex}`;
      if (this.storyGlydeCueKey === cueKey) return;
      this.storyGlydeCueKey = cueKey;
      maybeStartStoryGlydeApproach({
        missionId,
        cueId,
        lineIndex,
        requestDialogOnComplete: isReturnCue,
      });
    };

    if (shouldStageStoryGlyde) {
      buildStoryGlyde(activeStoryGlydeState);
      syncStoryGlydeDepth();

      if (shouldEnableStoryGlydeInteraction) {
        this.matter.add.rectangle(
          activeStoryGlydeState.x,
          activeStoryGlydeState.y - STORY_GLYDE_INTERACTION_OFFSET_Y,
          STORY_GLYDE_INTERACTION_SIZE.width,
          STORY_GLYDE_INTERACTION_SIZE.height,
          {
            isStatic: true,
            isSensor: true,
            label: "npc_glyde",
          },
        );
      }
    }

    if (shouldAutoApproachStoryGlyde) {
      this.time.delayedCall(0, () => {
        maybeStartStoryGlydeApproach({
          missionId: STORY_GLYDE_RETURN_MISSION_ID,
          cueId: STORY_GLYDE_AUTO_APPROACH_CUE_ID,
          lineIndex: 0,
          requestDialogOnComplete: true,
        });
      });
    }

    window.addEventListener("story-scene-cue", handleStorySceneCue);

    this.events.once("shutdown", () => {
      window.removeEventListener("story-scene-cue", handleStorySceneCue);
      this.storyGlyde?.destroy?.();
      this.storyGlyde = null;
      this.storyGlydeWalking = false;
      this.storyGlydeApproachComplete = false;
      this.storyGlydeCueKey = "";
    });
  }
}
